import { useState, useMemo, useCallback } from 'react';
import {
  useObservacoes,
  type CategoriaObservacao,
  type NivelAtencao,
  type CreateObservacaoInput,
  type Observacao,
} from '../../api/hooks/useObservacoes';
import { useMeusAlunosProfessor } from '../../api/hooks/useMeusAlunosProfessor';
import { useProfessorTurmas } from '../../api/hooks/useProfessorTurmas';
import { useAuthStore } from '../../store/authStore';
import { toast } from 'sonner';
import {
  isAvisoPedagogico,
  professorPodeEditarObservacao,
  OBSERVACAO_EDICAO_DIAS_PROFESSOR,
} from './observacoes-utils';
import {
  CATEGORIA_CONFIG,
  CATEGORIAS_RESTRITAS,
  CATEGORIAS_SIGILOSAS,
  PERFIS_ACESSO_TOTAL,
} from './observacoes-professor-shared';

const emptyForm = {
  alunoId: '',
  titulo: '',
  descricao: '',
  categoria: 'COMPORTAMENTO' as CategoriaObservacao,
  nivelAtencao: 'INFORMATIVO' as NivelAtencao,
  privado: true,
};

export function useObservacoesProfessorPage() {
  const user = useAuthStore((s) => s.user);
  const escolaId = useAuthStore((s) => s.escolaId);
  const perfil = useAuthStore((s) => s.perfil);
  const cargo = useAuthStore((s) => s.cargo);

  const temAcessoTotal =
    PERFIS_ACESSO_TOTAL.includes(perfil || '') ||
    ['DIRETOR', 'COORDENACAO'].includes(cargo || '');

  const categoriasDisponiveis = useMemo(() => {
    if (temAcessoTotal) return Object.entries(CATEGORIA_CONFIG);
    return Object.entries(CATEGORIA_CONFIG).filter(
      ([k]) => !CATEGORIAS_RESTRITAS.includes(k as CategoriaObservacao),
    );
  }, [temAcessoTotal]);

  const { turmas, turmaIds, isLoading: loadingGrade } = useProfessorTurmas(user?.id);
  const minhasTurmas = useMemo(
    () => turmas.map((t) => ({ id: t.id, nome: t.nome })),
    [turmas],
  );

  const { alunos: meusAlunos, isLoading: loadingAlunos } = useMeusAlunosProfessor(turmaIds);

  const alunoTurmaMap = useMemo(() => {
    const m = new Map<string, string>();
    meusAlunos.forEach((a) => {
      if (a.turmaId) m.set(a.id, a.turmaId);
    });
    return m;
  }, [meusAlunos]);

  const [filtroCategoria, setFiltroCategoria] = useState<CategoriaObservacao | ''>('');
  const [filtroNivel, setFiltroNivel] = useState<NivelAtencao | ''>('');
  const [filtroTurma, setFiltroTurma] = useState('');
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showAlunoPicker, setShowAlunoPicker] = useState(false);
  const [alunoSearch, setAlunoSearch] = useState('');
  const [pickerTurmaFilter, setPickerTurmaFilter] = useState('');
  const [showPickerFilter, setShowPickerFilter] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const { observacoes: observacoesRaw, isLoading, criar, atualizar, remover } = useObservacoes({
    autorId: user?.id,
    categoria: filtroCategoria || undefined,
    nivelAtencao: filtroNivel || undefined,
  });

  const observacoes = useMemo(() => {
    if (!filtroTurma) return observacoesRaw;
    return observacoesRaw.filter((o) => alunoTurmaMap.get(o.alunoId) === filtroTurma);
  }, [observacoesRaw, filtroTurma, alunoTurmaMap]);

  const observacaoSelecionada = useMemo(
    () => (selectedId ? observacoes.find((o) => o.id === selectedId) ?? null : null),
    [selectedId, observacoes],
  );

  const filtrosAtivos = !!(filtroCategoria || filtroNivel || filtroTurma);

  const stats = useMemo(() => {
    const total = observacoes.length;
    const urgentes = observacoes.filter((o) => o.nivelAtencao === 'URGENTE').length;
    const atencao = observacoes.filter((o) => o.nivelAtencao === 'ATENCAO').length;
    const avisos = observacoes.filter((o) => isAvisoPedagogico(o)).length;
    return { total, urgentes, atencao, avisos };
  }, [observacoes]);

  const resetForm = useCallback(() => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  }, []);

  const openEdit = useCallback((obs: Observacao) => {
    if (isAvisoPedagogico(obs)) {
      toast.info('Avisos a responsavel sao somente leitura nesta lista.');
      return;
    }
    if (!professorPodeEditarObservacao(obs)) {
      toast.error(
        `Observacoes com mais de ${OBSERVACAO_EDICAO_DIAS_PROFESSOR} dias sao somente leitura. Fale com a coordenacao.`,
      );
      return;
    }
    setForm({
      alunoId: obs.alunoId || '',
      titulo: obs.titulo || '',
      descricao: obs.descricao || '',
      categoria: obs.categoria || 'COMPORTAMENTO',
      nivelAtencao: obs.nivelAtencao || 'INFORMATIVO',
      privado: !!obs.privado,
    });
    setEditingId(obs.id);
    setSelectedId(null);
    setShowForm(true);
  }, []);

  const handleSalvar = () => {
    if (!form.titulo.trim() || !form.descricao.trim()) {
      toast.error('Preencha titulo e descricao');
      return;
    }
    if (!form.alunoId) {
      toast.error('Selecione um aluno');
      return;
    }
    if (!escolaId || !user?.id) {
      toast.error('Sessao invalida. Faca login novamente.');
      return;
    }

    if (editingId) {
      atualizar.mutate(
        {
          id: editingId,
          titulo: form.titulo.trim(),
          descricao: form.descricao.trim(),
          categoria: form.categoria,
          nivelAtencao: form.nivelAtencao,
          privado: form.privado,
        },
        {
          onSuccess: () => {
            toast.success('Observacao atualizada!');
            resetForm();
          },
          onError: () => toast.error('Erro ao atualizar'),
        },
      );
    } else {
      const input: CreateObservacaoInput = {
        alunoId: form.alunoId,
        autorId: user.id,
        escolaId,
        categoria: form.categoria,
        nivelAtencao: form.nivelAtencao,
        titulo: form.titulo.trim(),
        descricao: form.descricao.trim(),
        dataReferencia: new Date().toISOString().split('T')[0],
        privado: form.privado,
      };
      criar.mutate(input, {
        onSuccess: () => {
          toast.success('Observacao registrada!');
          resetForm();
        },
        onError: () => toast.error('Erro ao salvar observacao'),
      });
    }
  };

  const handleRemover = (id: string) => {
    if (!confirm('Deseja remover esta observacao?')) return;
    remover.mutate(id, {
      onSuccess: () => {
        toast.success('Observacao removida');
        setSelectedId(null);
      },
      onError: () => toast.error('Erro ao remover'),
    });
  };

  const openNovaObservacao = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  return {
    user,
    escolaId,
    temAcessoTotal,
    categoriasDisponiveis,
    minhasTurmas,
    meusAlunos,
    loadingAlunos,
    filtroCategoria,
    setFiltroCategoria,
    filtroNivel,
    setFiltroNivel,
    filtroTurma,
    setFiltroTurma,
    filtrosAbertos,
    setFiltrosAbertos,
    showForm,
    setShowForm,
    editingId,
    selectedId,
    setSelectedId,
    observacaoSelecionada,
    showAlunoPicker,
    setShowAlunoPicker,
    alunoSearch,
    setAlunoSearch,
    pickerTurmaFilter,
    setPickerTurmaFilter,
    showPickerFilter,
    setShowPickerFilter,
    form,
    setForm,
    observacoes,
    isLoading: isLoading || loadingGrade,
    filtrosAtivos,
    stats,
    resetForm,
    openEdit,
    handleSalvar,
    handleRemover,
    openNovaObservacao,
    criar,
    atualizar,
  };
}

export type ObservacoesProfessorPageContext = ReturnType<typeof useObservacoesProfessorPage>;
